import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Crosshair, X } from 'lucide-react';
import { useMapStore } from '@/stores/map-store';
import { useEntitySelectionStore } from '@/stores/entity-selection-store';
import { searchUnits } from '@/map/search/search-registry';
import type { SearchResult } from '@/map/search/search-provider';
import { cn } from '@/lib/utils';

/** Debounce before searching live units. */
const DEBOUNCE_MS = 200;

/** Find-unit search (callsign) docked under the Feed Layers — flies to + selects. */
export function UnitSearch(): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const seqRef = useRef(0);

  useEffect(() => {
    if (query.trim().length === 0) {
      setResults([]);
      return;
    }
    const handle = setTimeout(() => void run(query, seqRef, setResults), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  function pick(result: SearchResult): void {
    useMapStore.getState().setFlyTo({ lng: result.lon, lat: result.lat, zoom: result.zoom });
    if (result.uid) useEntitySelectionStore.getState().select(result.uid);
    setOpen(false);
    setQuery('');
    setResults([]);
  }

  return (
    <>
      {open ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: -4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.14 }}
          style={{ transformOrigin: 'top right' }}
          className="w-60 overflow-hidden rounded-lg border border-echo-border bg-echo-grey/92 shadow-2xl ring-1 ring-black/20 backdrop-blur"
        >
          <div className="flex items-center gap-2 px-2.5 py-2">
            <Crosshair className="h-4 w-4 shrink-0 text-echo-yellow" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => event.key === 'Escape' && setOpen(false)}
              placeholder="Find unit by callsign…"
              aria-label="Find a unit"
              className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
            />
            <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {results.length > 0 && (
            <div className="max-h-[40vh] overflow-y-auto border-t border-echo-border/60 p-1">
              {results.map((result, index) => (
                <button
                  key={`${result.uid ?? index}`}
                  type="button"
                  onClick={() => pick(result)}
                  className="flex w-full flex-col rounded-md px-2 py-1.5 text-left transition-colors hover:bg-echo-yellow/10"
                >
                  <span className="truncate text-[13px] text-foreground">{result.label}</span>
                  {result.sublabel && (
                    <span className="truncate text-[10px] text-muted-foreground">{result.sublabel}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </motion.div>
      ) : (
        <motion.button
          type="button"
          onClick={() => setOpen(true)}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.14 }}
          style={{ transformOrigin: 'top right' }}
          className="flex items-center gap-2 rounded-lg border border-echo-border bg-echo-grey/90 px-2.5 py-2 shadow-lg backdrop-blur transition-colors hover:border-echo-border/80"
        >
          <Crosshair className="h-4 w-4 text-muted-foreground" />
          <span className="font-chakra text-xs font-semibold uppercase tracking-wider text-foreground">Find Unit</span>
        </motion.button>
      )}
    </>
  );
}

/** Resolve units and publish only if still the latest query. */
async function run(
  query: string,
  seqRef: React.RefObject<number>,
  setResults: (results: SearchResult[]) => void,
): Promise<void> {
  const seq = (seqRef.current ?? 0) + 1;
  seqRef.current = seq;
  const found = await searchUnits(query);
  if (seqRef.current === seq) setResults(found);
}
